// Direct-to-Store uploads/downloads (08-contracts.md #4), driven by the
// allocation Core returns from POST /artifacts (id, upload_url,
// upload_token) or GET /artifacts/{id} (download_url, download_token).
//
// Core hands back an *absolute* upload_url/download_url
// (StorePublicURL + "/artifacts/" + id, 02-core.md #9) meant for a
// same-origin production deployment (deploy/Caddyfile routes /store/* to
// Store on the site's own public domain). A browser dev server on a
// different port can't follow that URL directly without Store serving
// CORS headers, which it doesn't (05-store.md: designed for
// server-to-server and CLI callers). So the UI always re-targets the
// *path* of whatever URL it was given at its own origin (Vite's /store
// proxy in dev, Caddy's /store/* route in production) instead -- this is
// a Phase 10 deviation, documented in docs/plan/notes/phase-10.md.
//
// The prefix is only added when the minted path does not already carry it.
// StorePublicURL is documented to *include* the public prefix
// (deploy/.env.example: "https://$PUBLIC_DOMAIN/store"; deploy/Caddyfile's
// `/store/* -> Store, prefix stripped`), so in the documented deployments
// the path Core mints is already `/store/artifacts/{id}` and adding
// STORE_BASE on top of it asks for `/store/store/...` -- a 404 on every
// upload and download. A StorePublicURL pointed straight at Store (no
// prefix) lands in the other branch and does need the prefix added.
import { STORE_BASE } from "./config";

function storePath(absoluteOrPath: string): string {
  let path = absoluteOrPath;
  try {
    path = new URL(absoluteOrPath).pathname;
  } catch {
    /* already a path */
  }
  const normalized = path.startsWith("/") ? path : "/" + path;
  if (normalized === STORE_BASE || normalized.startsWith(STORE_BASE + "/")) return normalized;
  return STORE_BASE + normalized;
}

/** The URL a plain `<img>`/`<video>` tag can load directly, no script
 * fetch required: the artifact's download URL with the read token in the
 * query string, which Store accepts for reads (ADR-0019), retargeted at
 * this origin the same way every other Store call is (`storePath` above)
 * so a dev server without CORS on Store can still load it. This is what
 * makes `loading="lazy"` thumbnails and native `<video>` Range-seeking
 * possible: fetching every tile's bytes into a blob up front (as the
 * Task Detail gallery does for its eager previews) would defeat both.
 * Undefined until Core has handed out a token. */
export function storeSrc(view?: { download_url?: string; download_token?: string } | null): string | undefined {
  if (!view?.download_url || !view.download_token) return undefined;
  const path = storePath(view.download_url);
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}t=${encodeURIComponent(view.download_token)}`;
}

export class StoreError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** PUT file's bytes to Store with the write token Core minted. Safe to
 * repeat (08-contracts.md #7: "upload artifact ... repeat -> 409 if
 * committed"); resuming an interrupted upload is just calling this again. */
export async function uploadToStore(uploadUrl: string, token: string, file: Blob, contentType: string): Promise<void> {
  const resp = await fetch(storePath(uploadUrl), {
    method: "PUT",
    headers: { Authorization: "Bearer " + token, "Content-Type": contentType || "application/octet-stream" },
    body: file,
  });
  if (resp.status !== 201 && resp.status !== 409) {
    throw new StoreError(resp.status, `store upload failed: ${resp.status}`);
  }
}

/** Fetches an artifact's bytes with the read token Core minted and
 * returns a local object URL for a <video>/<img> preview or a download
 * link -- Store's auth is a bearer token (08-contracts.md #4), which a
 * plain <a href>/<img src> can't attach, so the bytes must be fetched by
 * script either way. Callers must URL.revokeObjectURL() when done. */
export async function fetchStoreObjectURL(downloadUrl: string, token: string): Promise<string> {
  const blob = await fetchStoreBlob(downloadUrl, token);
  return URL.createObjectURL(blob);
}

/** The same bytes, as a byte array rather than a URL: a caller that has to
 * read the artifact rather than display it (the batch zip, lib/zip.ts)
 * cannot get at the contents of a blob: URL through <a href> semantics. */
export async function fetchStoreBytes(downloadUrl: string, token: string): Promise<Uint8Array<ArrayBuffer>> {
  const blob = await fetchStoreBlob(downloadUrl, token);
  return new Uint8Array(await blob.arrayBuffer());
}

export async function fetchStoreBlob(downloadUrl: string, token: string): Promise<Blob> {
  const resp = await fetch(storePath(downloadUrl), { headers: { Authorization: "Bearer " + token } });
  if (!resp.ok) {
    throw new StoreError(resp.status, `store download failed: ${resp.status}`);
  }
  return resp.blob();
}
